import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiTruck, FiPackage, FiClipboard, FiShield, FiCheck, FiChevronDown, FiChevronUp, FiMail, FiBox, FiTool, FiMonitor, FiLock } from 'react-icons/fi';
import api from '../services/api';

// ---------------------------------------------------------------------------
// Nav
// ---------------------------------------------------------------------------
const Nav = ({ logoUrl, companyName }) => (
  <nav className="bg-white shadow-sm sticky top-0 z-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between h-16 items-center">
        <div className="flex items-center space-x-3">
          {logoUrl && (
            <img src={logoUrl} alt={companyName || 'Logo'} className="h-10 w-auto" />
          )}
          <span className="text-xl font-bold text-brand-navy">3PL<span className="text-brand-gold">+</span></span>
        </div>
        <div className="hidden md:flex items-center space-x-8">
          <a href="#services" className="text-sm font-medium text-body hover:text-brand-navy">Services</a>
          <a href="#how-it-works" className="text-sm font-medium text-body hover:text-brand-navy">How It Works</a>
          <a href="#faq" className="text-sm font-medium text-body hover:text-brand-navy">FAQ</a>
          <a href="#contact" className="text-sm font-medium text-body hover:text-brand-navy">Contact</a>
          <Link
            to="/login"
            className="inline-flex items-center px-4 py-2 border-2 border-brand-navy text-sm font-medium rounded-md text-brand-navy hover:bg-brand-navy hover:text-white transition-colors"
          >
            Sign In
          </Link>
        </div>
        <div className="md:hidden">
          <Link
            to="/login"
            className="inline-flex items-center px-4 py-2 border-2 border-brand-navy text-sm font-medium rounded-md text-brand-navy hover:bg-brand-navy hover:text-white transition-colors"
          >
            Sign In
          </Link>
        </div>
      </div>
    </div>
  </nav>
);

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------
const Hero = () => (
  <section className="bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
      <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
        <div>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-tight text-brand-navy">
            Your inventory, managed with care
          </h1>
          <p className="mt-6 text-lg text-body leading-relaxed max-w-xl">
            We provide professional warehousing, fulfillment, and logistics services for businesses of all sizes. Store your inventory with us and manage everything through our online portal.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a
              href="#contact"
              className="inline-flex items-center justify-center px-6 py-3 border-2 border-brand-gold text-base font-medium rounded-md text-white bg-brand-gold hover:opacity-90 transition-opacity"
            >
              Get a Quote
            </a>
            <a
              href="#services"
              className="inline-flex items-center justify-center px-6 py-3 border-2 border-brand-navy text-base font-medium rounded-md text-brand-navy hover:bg-brand-navy hover:text-white transition-colors"
            >
              Our Services
            </a>
          </div>
        </div>
        <div className="hidden lg:block mt-12 lg:mt-0">
          <div className="rounded-2xl p-8 border-2 border-gray-100 bg-gray-50">
            <div className="space-y-4">
              {[
                'Heated, secured facility with insurance included',
                'No storage minimums, volume discounts available',
                'Inbound and outbound handling',
                'Order picking, asset tagging, and reboxing',
                'Technical services: testing, firmware, staging',
                'Exclusive 3PL portal access'
              ].map((item, i) => (
                <div key={i} className="flex items-center space-x-3">
                  <div className="w-6 h-6 rounded-full border-2 border-brand-gold flex items-center justify-center flex-shrink-0">
                    <FiCheck className="w-3 h-3 text-brand-gold" />
                  </div>
                  <span className="text-sm text-body">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ---------------------------------------------------------------------------
// Services
// ---------------------------------------------------------------------------
const services = [
  {
    icon: FiPackage,
    title: 'Warehousing & Storage',
    description: 'Heated, secured, insured facility. Standard and oversized pallet storage with no minimums and volume discounts for 20+ pallets.'
  },
  {
    icon: FiTruck,
    title: 'Fulfillment & Logistics',
    description: 'Inbound and outbound handling, order picking, shipping label preparation, and packing materials included. Domestic and international shipping available.'
  },
  {
    icon: FiTool,
    title: 'Technical Services',
    description: 'Returned equipment triage, verification, and testing. Firmware and software updates, product preparation, and phased deployments.'
  },
  {
    icon: FiBox,
    title: 'Value-Added Services',
    description: 'Asset tagging and staging, reboxing, white glove delivery, and removal of packaging. Everything you need to get products to your customers.'
  },
  {
    icon: FiMonitor,
    title: 'Customer Portal',
    description: 'Exclusive online portal to manage your inventory, submit inbound and outbound requests, track shipments, and view invoices.'
  },
  {
    icon: FiLock,
    title: 'Insurance & Security',
    description: 'Up to $50,000 coverage included with every account. Additional coverage available. Customer access to the facility Monday through Friday, 8am to 5pm.'
  }
];

const Services = () => (
  <section id="services" className="py-20 bg-gray-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-brand-navy">What we do for you</h2>
        <p className="mt-4 text-lg text-body max-w-2xl mx-auto">
          Full-service warehousing and logistics so you can focus on growing your business.
        </p>
      </div>
      <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {services.map((service, index) => (
          <div key={index} className="relative p-6 bg-white rounded-xl border border-gray-200 hover:border-brand-gold transition-colors">
            <div className="w-12 h-12 border-2 border-brand-navy rounded-lg flex items-center justify-center mb-4">
              <service.icon className="h-6 w-6 text-brand-navy" />
            </div>
            <h3 className="text-lg font-semibold text-brand-navy">{service.title}</h3>
            <p className="mt-2 text-body">{service.description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ---------------------------------------------------------------------------
// How It Works
// ---------------------------------------------------------------------------
const steps = [
  {
    number: '1',
    title: 'Get in touch',
    description: 'Contact us to discuss your storage and logistics needs. We will put together a plan and pricing that works for you.'
  },
  {
    number: '2',
    title: 'Send us your inventory',
    description: 'Ship your products to our warehouse. We receive, inspect, and catalog everything into our system.'
  },
  {
    number: '3',
    title: 'Manage it online',
    description: 'Log into your customer portal to view inventory, submit shipping or kitting requests, and track everything in real time.'
  },
  {
    number: '4',
    title: 'We handle the rest',
    description: 'Our team picks, packs, and ships your orders. You get notified at every step and can view invoices online.'
  }
];

const HowItWorks = () => (
  <section id="how-it-works" className="py-20 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-brand-navy">How it works</h2>
        <p className="mt-4 text-lg text-body max-w-2xl mx-auto">
          Getting started is straightforward. Here is what to expect.
        </p>
      </div>
      <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {steps.map((step, index) => (
          <div key={index} className="text-center">
            <div className="w-14 h-14 rounded-full border-2 border-brand-gold flex items-center justify-center mx-auto mb-4">
              <span className="text-xl font-bold text-brand-gold">{step.number}</span>
            </div>
            <h3 className="text-lg font-semibold text-brand-navy">{step.title}</h3>
            <p className="mt-2 text-body text-sm">{step.description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ---------------------------------------------------------------------------
// Pricing CTA
// ---------------------------------------------------------------------------
const PricingCTA = () => (
  <section id="pricing" className="py-16 bg-gray-50">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h2 className="text-3xl font-bold text-brand-navy">Interested in our services?</h2>
      <p className="mt-4 text-lg text-body">
        Every business is different. Contact us to schedule a call and we will put together pricing tailored to your storage and fulfillment needs.
      </p>
      <a
        href="#contact"
        className="mt-8 inline-flex items-center justify-center px-8 py-3 border-2 border-brand-gold text-base font-medium rounded-md text-white bg-brand-gold hover:opacity-90 transition-opacity"
      >
        Schedule a Call
      </a>
    </div>
  </section>
);

// ---------------------------------------------------------------------------
// FAQ
// ---------------------------------------------------------------------------
const faqItems = [
  {
    question: 'What types of products can you store?',
    answer: 'We handle a wide range of general merchandise, electronics, consumer goods, and commercial products. If you have specific storage requirements, contact us to discuss your needs.'
  },
  {
    question: 'How do I track my inventory?',
    answer: 'You get access to our customer portal where you can see real-time stock levels, view inbound and outbound history, and track individual items down to the serial number if needed.'
  },
  {
    question: 'How do I request a shipment?',
    answer: 'Log into the customer portal and submit a shipping request. Specify what you need shipped, the destination, and any special instructions. Our team handles the rest and you will be notified when it ships.'
  },
  {
    question: 'What is kitting and do you offer it?',
    answer: 'Kitting is assembling multiple individual items into a single package or kit. Yes, we offer kitting and assembly services. You define the kit components and quantities and we build them for you.'
  },
  {
    question: 'How does billing work?',
    answer: 'We invoice based on the services we provide, including storage, receiving, shipping, and kitting. You can view and download all your invoices through the customer portal. Rates are agreed upon when you sign up.'
  },
  {
    question: 'Can I have multiple users on my account?',
    answer: 'Yes. As a customer, you can invite additional users to your account and control what they have access to. This is great for teams where different people handle different aspects of your logistics.'
  },
  {
    question: 'How do I get started?',
    answer: 'Fill out the contact form below and we will be in touch. We can discuss your needs, tour the facility if you like, and get you set up with an account and portal access.'
  }
];

const FaqItem = ({ item }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-gray-200">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-5 flex justify-between items-center text-left focus:outline-none"
      >
        <span className="text-base font-medium text-brand-navy">{item.question}</span>
        {isOpen
          ? <FiChevronUp className="h-5 w-5 text-brand-gold flex-shrink-0 ml-4" />
          : <FiChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0 ml-4" />
        }
      </button>
      {isOpen && (
        <div className="pb-5 pr-12">
          <p className="text-body">{item.answer}</p>
        </div>
      )}
    </div>
  );
};

const FAQ = () => (
  <section id="faq" className="py-20 bg-white">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-brand-navy">Frequently asked questions</h2>
      </div>
      <div className="mt-12">
        {faqItems.map((item, index) => (
          <FaqItem key={index} item={item} />
        ))}
      </div>
    </div>
  </section>
);

// ---------------------------------------------------------------------------
// Contact
// ---------------------------------------------------------------------------
const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');

    try {
      await api.post('/contact', form);
      setSubmitted(true);
    } catch (error) {
      if (error.response?.status === 429) {
        setSubmitError('Too many submissions. Please try again later.');
      } else {
        setSubmitError(error.response?.data?.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="lg:grid lg:grid-cols-2 lg:gap-16">
          <div>
            <h2 className="text-3xl font-bold text-brand-navy">Let's talk</h2>
            <p className="mt-4 text-lg text-body">
              Tell us about your warehousing and logistics needs. We will get back to you within one business day to discuss how we can help.
            </p>
            <div className="mt-8 space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full border-2 border-brand-gold flex items-center justify-center">
                  <FiMail className="h-5 w-5 text-brand-gold" />
                </div>
                <span className="text-body">Contact us using the form</span>
              </div>
            </div>
          </div>
          <div className="mt-12 lg:mt-0">
            {submitted ? (
              <div className="border-2 border-brand-gold rounded-xl p-8 text-center bg-white">
                <div className="w-12 h-12 rounded-full border-2 border-brand-gold flex items-center justify-center mx-auto mb-4">
                  <FiCheck className="h-6 w-6 text-brand-gold" />
                </div>
                <h3 className="text-lg font-semibold text-brand-navy">Thank you!</h3>
                <p className="mt-2 text-body">We have received your message and will be in touch within one business day.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-8 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-body">Name</label>
                    <input id="contact-name" name="name" type="text" required value={form.name} onChange={handleChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-brand-gold focus:border-brand-gold sm:text-sm" />
                  </div>
                  <div>
                    <label htmlFor="contact-company" className="block text-sm font-medium text-body">Company</label>
                    <input id="contact-company" name="company" type="text" value={form.company} onChange={handleChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-brand-gold focus:border-brand-gold sm:text-sm" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-body">Email</label>
                    <input id="contact-email" name="email" type="email" required value={form.email} onChange={handleChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-brand-gold focus:border-brand-gold sm:text-sm" />
                  </div>
                  <div>
                    <label htmlFor="contact-phone" className="block text-sm font-medium text-body">Phone</label>
                    <input id="contact-phone" name="phone" type="tel" value={form.phone} onChange={handleChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-brand-gold focus:border-brand-gold sm:text-sm" />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-body">How can we help?</label>
                  <textarea id="contact-message" name="message" rows={4} value={form.message} onChange={handleChange}
                    placeholder="Tell us what you need -- storage, shipping, kitting, or something else..."
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-brand-gold focus:border-brand-gold sm:text-sm" />
                </div>
                {submitError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">{submitError}</div>
                )}
                <button type="submit" disabled={submitting}
                  className="w-full inline-flex justify-center py-3 px-4 border-2 border-brand-gold rounded-md text-sm font-medium text-white bg-brand-gold hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-gold disabled:opacity-50 transition-opacity">
                  {submitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------
const Footer = ({ logoUrl, companyName }) => (
  <footer className="bg-brand-navy text-gray-400">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="md:flex md:justify-between md:items-center">
        <div className="flex items-center space-x-3">
          {logoUrl && (
            <img src={logoUrl} alt={companyName || 'Logo'} className="h-8 w-auto brightness-0 invert" />
          )}
          <span className="text-lg font-bold text-white">3PL<span className="text-brand-gold">+</span></span>
        </div>
        <div className="mt-4 md:mt-0 flex space-x-6">
          <a href="#services" className="text-sm hover:text-white transition-colors">Services</a>
          <a href="#how-it-works" className="text-sm hover:text-white transition-colors">How It Works</a>
          <a href="#faq" className="text-sm hover:text-white transition-colors">FAQ</a>
          <a href="#contact" className="text-sm hover:text-white transition-colors">Contact</a>
          <Link to="/login" className="text-sm hover:text-white transition-colors">Sign In</Link>
        </div>
      </div>
      <div className="mt-8 border-t border-gray-700 pt-8 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 text-sm">
        <span>&copy; {new Date().getFullYear()} <a href="https://www.datavisual.ca" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">DataVisual Marketing Inc.</a> All rights reserved.</span>
        <div className="flex space-x-6">
          <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
        </div>
      </div>
    </div>
  </footer>
);

// ---------------------------------------------------------------------------
// Landing Page
// ---------------------------------------------------------------------------
const LandingPage = () => {
  const [branding, setBranding] = useState({
    company_name: null, site_title: null,
    has_landing_logo: false, has_favicon: false
  });

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await api.get('/system-settings/public-branding');
        setBranding(response.data);
      } catch (error) {
        // Silently fail -- landing page works without branding
      }
    };
    fetchBranding();
  }, []);

  const cacheBust = branding.updated_at ? new Date(branding.updated_at).getTime() : '';
  const logoUrl = branding.has_landing_logo
    ? `${api.defaults.baseURL}/system-settings/landing-logo?t=${cacheBust}`
    : null;
  const faviconUrl = branding.has_favicon
    ? `${api.defaults.baseURL}/system-settings/favicon?t=${cacheBust}`
    : null;
  const siteTitle = branding.site_title || 'Datavisual 3PL+';

  // Set the page title and favicon dynamically
  useEffect(() => {
    document.title = siteTitle;

    if (faviconUrl) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = faviconUrl;
    }
  }, [siteTitle, faviconUrl]);

  return (
    <div className="min-h-screen font-sans">
      <Nav logoUrl={logoUrl} companyName={branding.company_name} />
      <Hero />
      <Services />
      <HowItWorks />
      <PricingCTA />
      <FAQ />
      <Contact />
      <Footer logoUrl={logoUrl} companyName={branding.company_name} />
    </div>
  );
};

export default LandingPage;
